// Staff invoice detail + AR action surface. Hits GET /invoices/:id which
// returns the invoice row enriched with order/project/client attribution.
//
// Lifecycle actions (mark as sent / void) run through
// POST /invoices/:id/transition — the invoice state machine
// (services/invoices.ts):
//   open → sent_to_client → paid
//          ↘ voided (from any pre-paid state)
// Void requires a reason (inline form). Mutations are gated on admin /
// finance.
//
// SETTLEMENT GOES THROUGH THE PAYMENT LEDGER (2026-08-26). The old "Mark paid"
// button fired `mark_paid` directly, which flips a status and writes NO row to
// `invoice_payments` — so an invoice could read "paid" with an empty payment
// history, a part payment was unrecordable, and the open receivable never moved
// until the whole invoice flipped. "Record payment" replaces it:
// POST /invoices/:id/payments takes the amount actually received (partial or
// full, cheque / ACH / wire / cash / offline card) and the SERVER fires
// `mark_paid` in the same transaction when the entry clears the balance. The
// transition event still exists for Stripe and for that settlement — it is just
// no longer something a human can fire without recording the money.
//
// Layout (other Business UI handoff W14, 2026-10-08): 32px number header +
// status + facts row, the server's Balance due beside it, ONE dominant action
// (Send to client while unsent, then Record payment), secondaries as outline
// buttons, sections as white cards. Issuing, sending and recording a payment
// stay three separate events.
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { SentEmailsCard } from "@/components/send/SentEmailsCard";
import { PublicInvoicePage } from "@/pages/PublicInvoicePage";
import { VendorCafeInvoiceCard } from "@/components/vendor-cafe/VendorCafeInvoiceCard";
import { InvoiceItemsCard, ClientPoField } from "@/components/invoices/InvoiceItemsCard";
import {
  ArrowLeft,
  Ban,
  DollarSign,
  ExternalLink,
  FileMinus,
  Send,
  X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { Fact, FactsRow } from "@/components/ui/RecordParts";
import { useAuth } from "@/auth/AuthProvider";
import { cn } from "@/lib/utils";
import {
  SendToCustomerModal,
  type SendRecipientInput,
} from "@/components/send/SendToCustomerModal";

type InvoiceStatus =
  | "open"
  | "sent_to_client"
  | "exported"
  | "paid"
  | "voided";

type InvoiceType = "deposit" | "final";

type InvoiceTerms =
  | "net_15"
  | "net_30"
  | "net_45"
  | "net_60"
  | "net_90"
  | "credit_card"
  | "upfront";

interface InvoiceDetail {
  id: string;
  invoiceNumber: number;
  invoiceType: InvoiceType;
  status: InvoiceStatus;
  invoiceDate: string;
  subtotal: string;
  tax: string;
  total: string;
  depositApplied: string;
  balanceDue: string | null;
  /** Server-computed. balance_due nets deposits only; these net the ledger. */
  paidToDate: string;
  outstanding: string;
  paidAt: string | null;
  sentAt: string | null;
  voidedAt: string | null;
  /** Powers the "Preview invoice" button (/i/:uuid staff-preview pass). */
  publicShareUuid: string;
  terms: InvoiceTerms;
  /** YYYY-MM-DD — last delivery + terms (Stephanie 2026-10-16); null = waiting on delivery. */
  dueDate?: string | null;
  /** Client can pay by card online (the "Pay $X" button on their invoice). */
  allowCreditCard: boolean | null;
  orderId: string;
  orderNumber: number;
  /** The client's own PO number — kept on the quote / order. */
  customerPo?: string | null;
  projectId: string;
  projectName: string;
  clientId: string;
  clientName: string;
}

export interface InvoiceDetailResponse {
  invoice: InvoiceDetail;
  /** Whether a client-facing email was actually sent for this invoice.
   *  Derived server-side from email_sends. Keeps the "sent to client" status
   *  honest on reload — a link-only publish must never read as emailed. */
  email_sent?: boolean;
}

// ─── Credit memos (post-billing AR adjustments) ───────────────────────────
type CreditMemoReason =
  | "partial_refund"
  | "damage_reimbursement"
  | "pricing_correction"
  | "goodwill"
  | "return"
  | "other";

interface CreditMemo {
  id: string;
  /** Server-formatted 2dp dollar string — rendered as-is, never re-summed. */
  amount: string;
  amountCents: number;
  reason: CreditMemoReason | null;
  reasonNote: string | null;
  issuedAt: string;
  issuedById: string;
  issuedByName: string | null;
}

interface CreditMemoContext {
  invoiceId: string;
  invoiceStatus: string;
  /** All server-computed dollar strings — zero money math on the client. */
  invoiceTotal: string;
  creditedTotal: string;
  cap: string;
  capCents: number;
  canCreate: boolean;
  memos: CreditMemo[];
}

const REASON_OPTIONS: { value: CreditMemoReason; label: string }[] = [
  { value: "partial_refund", label: "Partial refund" },
  { value: "damage_reimbursement", label: "Damage reimbursement" },
  { value: "pricing_correction", label: "Pricing correction" },
  { value: "goodwill", label: "Goodwill" },
  { value: "return", label: "Return" },
  { value: "other", label: "Other" },
];

function reasonLabel(r: CreditMemoReason | null): string {
  if (!r) return "—";
  return REASON_OPTIONS.find((o) => o.value === r)?.label ?? r;
}

function todayIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Parse a dollar-string input into integer cents for the request body. This is
 *  request-serialisation only — the SERVER re-validates against the
 *  authoritative cap and is the source of truth. */
function dollarsToCents(input: string): number | null {
  const n = Number(input);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

function statusTone(
  s: InvoiceStatus,
): "active" | "info" | "dormant" | "closed" {
  switch (s) {
    case "open":
      return "info";
    case "sent_to_client":
    case "exported":
      return "info";
    case "paid":
      return "active";
    case "voided":
      return "closed";
  }
}

function statusLabel(s: InvoiceStatus): string {
  switch (s) {
    case "open":
      return "Not sent";
    case "sent_to_client":
      return "Sent to client";
    case "exported":
      return "Exported";
    case "paid":
      return "Paid";
    case "voided":
      return "Voided";
  }
}

function formatMoney(s: string | null): string {
  if (s === null) return "—";
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTerms(t: InvoiceTerms): string {
  switch (t) {
    case "net_15":
      return "Net 15";
    case "net_30":
      return "Net 30";
    case "net_45":
      return "Net 45";
    case "net_60":
      return "Net 60";
    case "net_90":
      return "Net 90";
    case "credit_card":
      return "Credit card";
    case "upfront":
      return "Upfront";
  }
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string } | undefined;
    return body?.error ?? `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

/** Friendly copy for the stable credit-memo error codes the API returns. */
function memoErrText(e: unknown): string {
  if (e instanceof ApiError) {
    const code = (e.body as { error?: string } | undefined)?.error;
    switch (code) {
      case "invoice_wrong_state":
        return "This invoice can't take a credit memo in its current state.";
      case "amount_over_cap":
        return "That amount is more than the invoice's remaining creditable balance.";
      case "amount_not_positive":
        return "Enter a credit amount greater than $0.";
      case "invoice_not_found":
        return "This invoice no longer exists.";
      case "memo_not_found":
        return "That credit memo no longer exists.";
      default:
        return code ?? `Request failed (${e.status})`;
    }
  }
  return e instanceof Error ? e.message : "Something went wrong";
}


// ─── Payments ─────────────────────────────────────────────────────────────
// The ledger behind GET /invoices/:id/payments. EVERY money figure here is a
// server-computed string — this page renders them and never sums them (the
// house rule that keeps the Finance dashboard and this screen agreeing).

type PaymentMethod = "check" | "ach" | "wire" | "cash" | "card" | "other";

const METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: "check", label: "Check" },
  { value: "ach", label: "ACH / bank transfer" },
  { value: "wire", label: "Wire" },
  { value: "card", label: "Credit card (taken offline)" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
];

function methodLabel(v: string): string {
  return METHOD_OPTIONS.find((o) => o.value === v)?.label ?? v;
}

interface PaymentRow {
  id: string;
  amount: string;
  amountCents: number;
  method: string;
  status: string;
  paidAt: string | null;
  reference: string | null;
  note: string | null;
  isOnline: boolean;
  recordedByName: string | null;
}

interface PaymentContext {
  invoiceId: string;
  invoiceStatus: InvoiceStatus;
  invoiceTotal: string;
  depositApplied: string;
  netTotal: string;
  paid: string;
  paidCents: number;
  outstanding: string;
  outstandingCents: number;
  defaultAmountCents: number;
  canRecord: boolean;
  payments: PaymentRow[];
}

/** Server refusal codes → the copy an operator can act on. */
function paymentErrText(e: unknown): string {
  const code =
    e instanceof ApiError
      ? ((e.body as { error?: string } | null)?.error ?? null)
      : null;
  switch (code) {
    case "amount_over_outstanding":
      return "That is more than the outstanding balance. Record the amount actually received — an overpayment is handled with a credit memo.";
    case "nothing_outstanding":
      return "This invoice has no outstanding balance.";
    case "invoice_not_payable":
      return "This invoice can't take a payment in its current status.";
    case "amount_not_positive":
    case "invalid_amount":
      return "Enter a payment amount greater than $0.";
    case "would_unpay_settled_invoice":
      return "This invoice is already settled, so the payment can't be reversed here. Issue a credit memo instead.";
    case "payment_not_reversible":
      return "That payment has already been reversed.";
    case "payment_not_found":
      return "That payment is no longer on this invoice.";
    default:
      return errText(e);
  }
}

type InvoiceEvent = "send_to_client" | "mark_paid" | "void";

function okMsg(event: InvoiceEvent): string {
  switch (event) {
    case "send_to_client":
      return "Invoice sent to the client.";
    case "mark_paid":
      return "Invoice marked paid.";
    case "void":
      return "Invoice voided.";
  }
}

export function InvoiceDetailPage() {
  // Nested under /projects/:id, `id` is the PROJECT id and `invoiceId` is the
  // invoice — top-level /invoices/:id has no invoiceId. Resolve once to the
  // document id so every `/invoices/${id}` call below hits the right resource
  // (2026-07-25).
  const { id: routeId, invoiceId } = useParams<{
    id: string;
    invoiceId: string;
  }>();
  const id = invoiceId ?? routeId;
  // Nested (in-project, invoiceId present) vs standalone (top-level
  // /invoices/:id via the InvoiceRedirect fallback). When nested, the
  // ProjectDetailLayout chrome (breadcrumb band + left sub-nav) already carries
  // the client/project context, so our own "← Invoicing" back-link + "client ·
  // project" line would duplicate it — suppress both, and widen to max-w-none
  // to fill the layout's 1fr column. Standalone keeps them + the max-w-6xl
  // frame (no project chrome to defer to there). Mirrors OrderDetailPage's
  // density pass (CEO 2026-07-25; ProjectDetailPage.tsx chrome).
  const nested = Boolean(invoiceId);
  const container = "mx-auto w-full"; // full width (CEO 2026-10-14)
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage =
    user?.roles.some((r) => r === "admin" || r === "finance") ?? false;

  const detailKey = ["invoices", id, "detail"];
  const { data, isLoading, isError } = useQuery({
    queryKey: detailKey,
    queryFn: () => api<InvoiceDetailResponse>(`/invoices/${id}`),
    enabled: Boolean(id),
  });

  // Credit-memo panel context (existing memos + server-computed cap + net
  // position). Staff finance/admin only — mirrors the mutation gate.
  const memosKey = ["invoices", id, "credit-memos"];
  const memosQ = useQuery({
    queryKey: memosKey,
    queryFn: () => api<CreditMemoContext>(`/invoices/${id}/credit-memos`),
    enabled: Boolean(id) && canManage,
  });

  // Payment ledger + the server's own paid / outstanding figures. Same staff
  // gate as the mutations, so a rep without finance never fetches it.
  const paymentsKey = ["invoices", id, "payments"];
  const paymentsQ = useQuery({
    queryKey: paymentsKey,
    queryFn: () => api<PaymentContext>(`/invoices/${id}/payments`),
    enabled: Boolean(id) && canManage,
  });

  const [banner, setBanner] = useState<{ kind: "ok" | "err"; msg: string } | null>(
    null,
  );
  // Record-payment modal state.
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<PaymentMethod>("check");
  const [payDate, setPayDate] = useState(todayIso());
  const [payReference, setPayReference] = useState("");
  const [payNote, setPayNote] = useState("");
  const [payError, setPayError] = useState<string | null>(null);
  const [voidMode, setVoidMode] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  // Credit-memo create modal state.
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [memoAmount, setMemoAmount] = useState("");
  const [memoReason, setMemoReason] = useState<CreditMemoReason>("partial_refund");
  const [memoNote, setMemoNote] = useState("");
  const [memoDate, setMemoDate] = useState(todayIso());
  const [memoError, setMemoError] = useState<string | null>(null);
  // Set when send_to_client hit 409 no_client_contact — drives the
  // add-contact / share-link-only prompt instead of a plain error line
  // (mirrors the estimate + presentation send flows).
  const [noContactPrompt, setNoContactPrompt] = useState(false);
  // "Send to client" window (Stephanie 2026-10-13 — same window as the
  // quote): pick contacts, edit the email, send. The missing-link confirm
  // keeps the window open and arms a second click.
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendModalMsg, setSendModalMsg] = useState<string | null>(null);
  const [sendModalNotice, setSendModalNotice] = useState<string | null>(null);
  const [missingLinkConfirmPending, setMissingLinkConfirmPending] =
    useState(false);

  function closeSendModal() {
    setShowSendModal(false);
    setSendModalMsg(null);
    setSendModalNotice(null);
    setMissingLinkConfirmPending(false);
  }

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: detailKey });
    void queryClient.invalidateQueries({ queryKey: ["sent-emails"] });
  };

  const transition = useMutation({
    mutationFn: (body: {
      event: InvoiceEvent;
      void_reason?: string;
      publish_link_only?: boolean;
      recipients?: SendRecipientInput[];
      body_html?: string;
      skip_missing_link_check?: boolean;
      send_copy_to_me?: boolean;
      bcc?: Array<{ email: string; name?: string }>;
    }) =>
      api<{ email_sent?: boolean }>(`/invoices/${id}/transition`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (res, vars) => {
      setNoContactPrompt(false);
      closeSendModal();
      // Honest confirmation — never claim "emailed" on a link-only publish.
      const msg =
        vars.event === "send_to_client" && res.email_sent === false
          ? "Link shared — no email sent. Copy the invoice link to give it to the client."
          : okMsg(vars.event);
      setBanner({ kind: "ok", msg });
      setVoidMode(false);
      setVoidReason("");
      invalidate();
    },
    onError: (e, vars) => {
      const body = e instanceof ApiError
        ? (e.body as { error?: string; reason?: string; message?: string } | null)
        : null;
      // Missing-link confirm: keep the window open (recipients + email kept)
      // and arm "Send anyway".
      if (
        vars.event === "send_to_client" &&
        e instanceof ApiError &&
        e.status === 409 &&
        body?.reason === "missing_document_link"
      ) {
        setSendModalMsg(null);
        setMissingLinkConfirmPending(true);
        setSendModalNotice(
          body.message ??
            "This email doesn't include the invoice link — the client won't be able to open it. Send anyway?",
        );
        return;
      }
      // Picked people but none has an email — say so inside the window.
      if (
        vars.event === "send_to_client" &&
        (vars.recipients?.length ?? 0) > 0 &&
        e instanceof ApiError &&
        e.status === 409 &&
        body?.error === "no_client_contact"
      ) {
        setSendModalMsg(body.message ?? "None of the picked people has an email address.");
        return;
      }
      // Any other failure from the window shows in the window.
      if (vars.event === "send_to_client" && vars.recipients) {
        setSendModalMsg(body?.message ?? body?.error ?? errText(e));
        return;
      }
      // 409 no_client_contact on send_to_client is a decision point, not a
      // plain error — surface the add-contact / share-link-only prompt.
      if (
        vars.event === "send_to_client" &&
        e instanceof ApiError &&
        e.status === 409 &&
        (e.body as { error?: string } | null)?.error === "no_client_contact"
      ) {
        setBanner(null);
        setNoContactPrompt(true);
        return;
      }
      setNoContactPrompt(false);
      setBanner({ kind: "err", msg: errText(e) });
    },
  });

  // "Allow card payment" switch (Stephanie 2026-10-13: off by default so
  // clients are nudged to pay by check; switch on per invoice).
  const cardPayment = useMutation({
    mutationFn: (allow: boolean) =>
      api<{ allow_credit_card: boolean }>(`/invoices/${id}/card-payment`, {
        method: "PATCH",
        body: JSON.stringify({ allow }),
      }),
    onSuccess: () => invalidate(),
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  const invalidateMemos = () =>
    void queryClient.invalidateQueries({ queryKey: memosKey });

  const createMemo = useMutation({
    mutationFn: (body: {
      amount_cents: number;
      reason: CreditMemoReason;
      reason_note: string;
      issued_at: string;
    }) =>
      api<{ memo: CreditMemo }>(`/invoices/${id}/credit-memos`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setShowMemoModal(false);
      setMemoAmount("");
      setMemoNote("");
      setMemoReason("partial_refund");
      setMemoDate(todayIso());
      setMemoError(null);
      setBanner({ kind: "ok", msg: "Credit memo created." });
      invalidateMemos();
      // The memo changes the invoice's net position; refresh the header too.
      invalidate();
    },
    onError: (e) => setMemoError(memoErrText(e)),
  });

  const invalidatePayments = () =>
    void queryClient.invalidateQueries({ queryKey: paymentsKey });

  const recordPayment = useMutation({
    mutationFn: (body: {
      amount_cents: number;
      method: PaymentMethod;
      paid_at: string;
      reference?: string;
      note?: string;
    }) =>
      api<{ settled_invoice: boolean }>(`/invoices/${id}/payments`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (res) => {
      setShowPayModal(false);
      setPayAmount("");
      setPayReference("");
      setPayNote("");
      setPayMethod("check");
      setPayDate(todayIso());
      setPayError(null);
      // Say which of the two things happened — "payment recorded" on an invoice
      // that is now settled reads as though something is still owed.
      setBanner({
        kind: "ok",
        msg: res.settled_invoice
          ? "Payment recorded — invoice paid in full."
          : "Payment recorded. The remaining balance is still outstanding.",
      });
      invalidatePayments();
      // Settling flips the invoice status and clears the balance in the header.
      invalidate();
    },
    onError: (e) => setPayError(paymentErrText(e)),
  });

  const reversePayment = useMutation({
    mutationFn: (paymentId: string) =>
      api<{ ok: true }>(`/invoices/${id}/payments/${paymentId}/reverse`, {
        method: "POST",
      }),
    onSuccess: () => {
      setBanner({ kind: "ok", msg: "Payment reversed." });
      invalidatePayments();
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: paymentErrText(e) }),
  });

  const voidMemo = useMutation({
    mutationFn: (memoId: string) =>
      api<{ ok: true }>(`/invoices/${id}/credit-memos/${memoId}/void`, {
        method: "POST",
      }),
    onSuccess: () => {
      setBanner({ kind: "ok", msg: "Credit memo voided." });
      invalidateMemos();
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: memoErrText(e) }),
  });

  if (isLoading) {
    return (
      <div className={container} aria-busy="true">
        <div className="mt-2 h-3 w-28 animate-pulse rounded bg-canvas" />
        <div className="mt-3 h-9 w-56 animate-pulse rounded bg-canvas" />
        <div className="mt-6 h-40 w-full animate-pulse rounded-md bg-canvas" />
        <p className="sr-only">Loading invoice…</p>
      </div>
    );
  }

  if (isError || !data?.invoice) {
    return (
      <div className={container}>
        <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-5 text-sm text-risk">
          We couldn't load this invoice. Nothing has changed — refresh to try again.
        </div>
        <Link
          to="/finance/invoicing"
          className="mt-4 inline-flex min-h-[40px] items-center gap-1 text-sm text-ink hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to invoicing
        </Link>
      </div>
    );
  }

  const invoice = data.invoice;
  const status = invoice.status;
  // Published link-only: the invoice link is live but no email was ever sent.
  // "sent to client" would imply the client was emailed — say "Link shared"
  // instead. Only the ambiguous sent_to_client badge is overridden; paid /
  // voided reflect real later actions. Mirrors PresentationDetailPage.
  const emailSent = data.email_sent ?? false;
  const showLinkOnlyBadge = status === "sent_to_client" && !emailSent;

  // Available transitions per the invoices.ts state machine.
  const canSend = status === "open";
  // An invoice already sent (or in QuickBooks) can be emailed again (Stephanie
  // 2026-11-03: "it does not give me an option to send it again").
  const canSendAgain = status === "sent_to_client" || status === "exported";
  const canVoid = ["open", "sent_to_client", "exported"].includes(status);
  // Settling an invoice goes through the PAYMENT ledger, not a bare status
  // flip. The `mark_paid` transition still exists (Stripe fires it, and the
  // payment route fires it on full settlement) but it is no longer offered as a
  // button: clicking it wrote no ledger row, so an invoice could read "paid"
  // with an empty payment history and nothing to reconcile a bank statement
  // against. The server decides eligibility (state + outstanding > 0).
  const payCtx = paymentsQ.data;
  const canRecordPayment = canManage && (payCtx?.canRecord ?? false);
  // Credit memos are a post-billing adjustment, so they stay available on paid
  // invoices too — the server decides eligibility (state + remaining cap).
  const memoCtx = memosQ.data;
  const canCreateMemo = canManage && (memoCtx?.canCreate ?? false);
  const hasActions =
    canManage && (canSend || canSendAgain || canRecordPayment || canVoid || canCreateMemo);

  // What is still OWED — server-computed as balance_due minus the ledgered
  // payments, floored at 0. This replaces a display hack that special-cased
  // `status === "paid"` to print $0.00, which existed only because the
  // generated balance_due column never moved when an invoice was settled. Now
  // the number is honest at every point in between, part payments included.
  const balanceDisplay = formatMoney(invoice.outstanding);

  function shareLinkOnly() {
    setBanner(null);
    transition.mutate({ event: "send_to_client", publish_link_only: true });
  }

  function confirmVoid() {
    const reason = voidReason.trim();
    if (reason.length === 0) return;
    transition.mutate({ event: "void", void_reason: reason });
  }

  // Upfront payments (handoff W14). A deposit-type invoice IS the client's
  // upfront payment (website checkout records the card payment this way; a rep
  // order's approve-and-pay mints one too). The final invoice then nets it via
  // depositApplied — on a website order that leaves $0 due, which must read
  // as "already paid", not as a zero-value invoice.
  const isUpfrontInvoice = invoice.invoiceType === "deposit";
  const hasUpfrontNetted = Number(invoice.depositApplied) > 0;
  const nothingOutstanding = Number(invoice.outstanding) === 0;
  const isVoided = status === "voided";

  // ONE dominant action: issue → send, then collect. Everything else is an
  // outline secondary. Sending and recording a payment are separate events.
  const primary: "send" | "pay" | null = canManage
    ? canSend
      ? "send"
      : canRecordPayment
        ? "pay"
        : null
    : null;

  function openPayModal() {
    setPayError(null);
    // Pre-fill the whole outstanding balance — paid in full is by far the
    // common case — but it is only a default; the operator types over it for
    // a part payment and the server re-derives its own outstanding either way.
    setPayAmount(payCtx ? (payCtx.outstandingCents / 100).toFixed(2) : "");
    setPayDate(todayIso());
    setShowPayModal(true);
  }

  return (
    <div className={container}>
      {/* Back link — the AR queue at /finance/invoicing is the canonical
          invoice list. Rendered only when standalone: nested inside
          ProjectDetailLayout, the chrome's breadcrumb band + side nav are the
          way back, and a hardcoded "← Invoicing" link would eject the rep
          from the project workspace (CEO 2026-07-25; ProjectDetailPage.tsx). */}
      {!nested && (
        <Link
          to="/finance/invoicing"
          className="mb-2 inline-flex min-h-[32px] items-center gap-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted hover:text-ink"
        >
          <ArrowLeft className="h-3 w-3" /> Invoicing
        </Link>
      )}

      {/* Record header: number + status + facts, balance due on the right. */}
      <header className="mb-6 flex flex-wrap items-start justify-between gap-x-8 gap-y-5">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            {/* Every invoice is just an invoice (Stephanie 2026-10-27). */}
            Invoice
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
              Invoice #{invoice.invoiceNumber}
            </h1>
            <StatusPill className="normal-case"
              tone={showLinkOnlyBadge ? "warn" : statusTone(invoice.status)}
            >
              {showLinkOnlyBadge
                ? "Link shared — no email sent"
                : statusLabel(invoice.status)}
            </StatusPill>
          </div>
          {/* client · project — suppressed when nested (the project chrome
              already shows it); kept standalone. */}
          {!nested && (
            <p className="mt-1 text-sm text-muted">
              {invoice.clientName} · {invoice.projectName}
            </p>
          )}
          <FactsRow>
            <Fact label="Quote">
              <Link
                to={`/projects/${invoice.projectId}/quotes/${invoice.orderId}`}
                className="underline-offset-4 hover:underline"
              >
                #{invoice.orderNumber}
              </Link>
            </Fact>
            <Fact label="Invoice date">{formatDate(invoice.invoiceDate)}</Fact>
            {/* A prepay invoice says so in its terms (Stephanie 2026-10-27). */}
            <Fact label="Terms">{isUpfrontInvoice ? "Prepay" : formatTerms(invoice.terms)}</Fact>
            {/* No Due date here either (Stephanie 2026-11-02: "hide the due date on the
                staff page too") — delivery isn't known yet. Flow still works it out
                (last delivery + terms) for overdue reminders and aging. */}
            <Fact label="Sent">{invoice.sentAt ? formatDate(invoice.sentAt) : "Not yet"}</Fact>
            {invoice.paidAt && <Fact label="Paid">{formatDate(invoice.paidAt)}</Fact>}
            {invoice.voidedAt && <Fact label="Voided">{formatDate(invoice.voidedAt)}</Fact>}
          </FactsRow>
        </div>

        {/* Balance due — the server's `outstanding` (balance_due minus the
            ledgered payments, floored at 0). Never computed here. */}
        <div className="w-full rounded-md border border-rule bg-white px-5 py-4 sm:w-auto sm:min-w-[220px] sm:text-right">
          <p className="text-xs text-muted">Balance due</p>
          <p
            className={cn(
              "mt-1 text-[32px] font-semibold leading-tight tracking-[-1px] tabular-nums",
              isVoided ? "text-muted line-through" : "text-ink",
            )}
          >
            {balanceDisplay}
          </p>
          <p className="mt-1 text-xs text-muted">
            of {formatMoney(invoice.total)} invoiced
          </p>
        </div>
      </header>

      {/* Actions. Preview is stage-agnostic and outside the hasActions gate:
          pre-send it opens the staff-preview pass (the public route 404s
          anonymous traffic until "Send to client" stamps sentAt), post-send it
          shows exactly what the client sees. */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            window.open(
              `/i/${invoice.publicShareUuid}`,
              "_blank",
              "noopener,noreferrer",
            )
          }
          title="Open the client view in a new tab — shows the invoice as the client will see it. Before you send it to the client, only staff can open this link."
        >
          <ExternalLink className="h-4 w-4" /> Preview invoice
        </Button>
        {hasActions && (
          <>
            {canSend && (
              <Button
                type="button"
                variant={primary === "send" ? "primary" : "outline"}
                onClick={() => {
                  setBanner(null);
                  setNoContactPrompt(false);
                  setShowSendModal(true);
                }}
                disabled={transition.isPending}
                title="Pick who gets it, check the email, and send the invoice link. Marks the invoice sent."
              >
                <Send className="h-4 w-4" />
                Send to client
              </Button>
            )}
            {canSendAgain && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setBanner(null);
                  setNoContactPrompt(false);
                  setShowSendModal(true);
                }}
                disabled={transition.isPending}
                title="Email the invoice link again — pick who gets it and check the email."
              >
                <Send className="h-4 w-4" />
                Send again
              </Button>
            )}
            {canRecordPayment && (
              <Button
                type="button"
                variant={primary === "pay" ? "primary" : "outline"}
                onClick={openPayModal}
              >
                <DollarSign className="h-4 w-4" /> Record payment
              </Button>
            )}
            {canCreateMemo && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setMemoError(null);
                  setMemoAmount("");
                  setMemoReason("partial_refund");
                  setMemoNote("");
                  setMemoDate(todayIso());
                  setShowMemoModal(true);
                }}
              >
                <FileMinus className="h-4 w-4" /> Create credit memo
              </Button>
            )}
            {canVoid && (
              <Button
                type="button"
                variant="outline"
                className="text-risk"
                aria-expanded={voidMode}
                onClick={() => {
                  setVoidMode((v) => !v);
                  setVoidReason("");
                  setBanner(null);
                }}
              >
                <Ban className="h-4 w-4" /> Void
              </Button>
            )}
          </>
        )}
      </div>

      {/* Result of the last action — says what happened. */}
      {banner && (
        <div
          role={banner.kind === "err" ? "alert" : "status"}
          className={cn(
            "mb-6 rounded-md border px-4 py-3 text-sm",
            banner.kind === "ok"
              ? "border-ok/30 bg-ok-bg text-ok"
              : "border-risk/30 bg-risk-bg text-risk",
          )}
        >
          {banner.kind === "err" && <span className="font-medium">Nothing changed. </span>}
          {banner.msg}
        </div>
      )}

      {/* No emailable contact — 409 decision point. Offer to publish the
          link without emailing (honest link-only send) or cancel. */}
      {noContactPrompt && (
        <div role="alert" className="mb-6 rounded-md border border-warn/30 bg-warn-bg px-4 py-4 text-sm text-ink">
          <p className="font-medium text-warn">No emailable contact</p>
          <p className="mt-1">
            This client has no contact with an email address, so we didn&apos;t
            change the invoice. Add a contact to email it — or share the link
            without emailing (the status will read &ldquo;Link shared — no
            email sent&rdquo;).
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button type="button" onClick={shareLinkOnly} disabled={transition.isPending}>
              {transition.isPending ? "Sharing…" : "Share link without emailing"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setNoContactPrompt(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Void reason form */}
      {voidMode && canManage && (
        <div className="mb-6 rounded-md border border-risk/30 bg-white p-[22px]">
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Void this invoice</h2>
          <p className="mt-1 text-sm text-muted">
            Voiding cancels the whole invoice and is kept in the history. To
            reduce what's owed instead, create a credit memo.
          </p>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="min-w-[220px] flex-1 text-xs text-muted">
              Reason (required)
              <input
                type="text"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Why is this invoice being voided?"
                className={FIELD}
              />
            </label>
            <Button
              type="button"
              variant="risk"
              disabled={transition.isPending || voidReason.trim().length === 0}
              onClick={confirmVoid}
            >
              {transition.isPending ? "Saving…" : "Confirm void"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setVoidMode(false);
                setVoidReason("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Upfront-payment context — make netting legible. */}
      {isUpfrontInvoice && (
        <p className="mb-6 rounded-md border border-rule bg-white px-4 py-3 text-sm text-ink">
          This invoice records the client&apos;s upfront payment. The order&apos;s
          final invoice nets it, so the client is never billed twice.
        </p>
      )}
      {!isUpfrontInvoice && hasUpfrontNetted && (
        <p className="mb-6 rounded-md border border-ok/30 bg-ok-bg px-4 py-3 text-sm text-ok">
          {nothingOutstanding
            ? `Paid upfront — this invoice nets the ${formatMoney(invoice.depositApplied)} upfront payment, so nothing more is due.`
            : `${formatMoney(invoice.depositApplied)} was paid upfront and is netted below.`}
        </p>
      )}

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(260px,1fr)]">
        <div className="min-w-0 space-y-6">
          {id && <InvoiceItemsCard invoiceId={id} orderId={invoice.orderId} />}
          {/* Amounts — every figure is a server string. */}
          <section className={CARD}>
            <h2 className={CARD_TITLE}>Amounts</h2>
            <dl className="mt-3 divide-y divide-[#e7e7e4] text-sm">
              <TotalsRow label="Subtotal" value={formatMoney(invoice.subtotal)} />
              {Number(invoice.tax) !== 0 && <TotalsRow label="Tax" value={formatMoney(invoice.tax)} />}
              <TotalsRow label="Invoice total" value={formatMoney(invoice.total)} emphasis />
              {hasUpfrontNetted && (
                <TotalsRow
                  label="Paid upfront"
                  value={`− ${formatMoney(invoice.depositApplied)}`}
                />
              )}
              {Number(invoice.paidToDate) > 0 && (
                <TotalsRow
                  label="Payments received"
                  value={`− ${formatMoney(invoice.paidToDate)}`}
                />
              )}
              <TotalsRow label="Balance due" value={balanceDisplay} emphasis />
            </dl>
            <p className="mt-3 text-xs text-muted">
              Until it&apos;s paid or in QuickBooks, remove an item with its bin
              icon in Items above; add one from the quote (each item&apos;s “Add to
              invoice” / “Copy to invoice”). After that, corrections go through a
              credit memo or a void, and both stay in the history.
            </p>
          </section>

          {/* Payments — the ledger behind the balance. Staff finance only.
              Shown whenever a payment exists OR one can be recorded, so an
              unpaid invoice still offers the entry point and a settled one
              still shows what was received and when. */}
          {canManage && payCtx && (payCtx.payments.length > 0 || payCtx.canRecord) && (
            <section className={CARD}>
              <h2 className={CARD_TITLE}>Payments</h2>
              {payCtx.payments.length === 0 ? (
                <p className="mt-3 text-sm text-muted">
                  No payments recorded. {formatMoney(payCtx.outstanding)}{" "}
                  outstanding.
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-[#e7e7e4]">
                  {payCtx.payments.map((pmt) => {
                    const reversed = pmt.status === "refunded";
                    return (
                      <li
                        key={pmt.id}
                        className="flex flex-wrap items-start justify-between gap-3 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm">
                            <span
                              className={cn(
                                "font-medium tabular-nums",
                                reversed ? "text-muted line-through" : "text-ink",
                              )}
                            >
                              {formatMoney(pmt.amount)}
                            </span>{" "}
                            <span className="text-muted">
                              · {methodLabel(pmt.method)}
                              {pmt.isOnline ? " (online)" : ""}
                            </span>
                            {reversed && (
                              <StatusPill tone="closed" className="ml-2 normal-case">Reversed</StatusPill>
                            )}
                          </p>
                          {pmt.reference && (
                            <p className="mt-0.5 text-sm text-muted">Ref {pmt.reference}</p>
                          )}
                          {pmt.note && <p className="mt-0.5 text-sm text-muted">{pmt.note}</p>}
                          <p className="mt-0.5 text-xs text-muted">
                            Received {pmt.paidAt ? formatDate(pmt.paidAt) : "—"}
                            {pmt.recordedByName ? ` · recorded by ${pmt.recordedByName}` : ""}
                          </p>
                        </div>
                        {/* Reverse is offered only on a live payment, and the
                            server refuses it on a settled invoice (there is
                            no backward edge out of 'paid' — a credit memo is
                            the audited way to move money off one). */}
                        {!reversed && !pmt.isOnline && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => reversePayment.mutate(pmt.id)}
                            disabled={reversePayment.isPending}
                          >
                            Reverse
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {payCtx.payments.length > 0 && (
                <dl className="mt-1 divide-y divide-[#e7e7e4] border-t border-rule text-sm">
                  <TotalsRow label="Received to date" value={formatMoney(payCtx.paid)} />
                  <TotalsRow label="Still outstanding" value={formatMoney(payCtx.outstanding)} emphasis />
                </dl>
              )}
            </section>
          )}

          {/* Credit memos — post-billing AR adjustments. Staff finance only;
              hidden entirely for non-managers. Net position + amounts are all
              server-computed strings (zero money math here). */}
          {canManage && memoCtx && (memoCtx.memos.length > 0 || canCreateMemo) && (
            <section className={CARD}>
              <h2 className={CARD_TITLE}>Credit memos</h2>
              {memoCtx.memos.length === 0 ? (
                <p className="mt-3 text-sm text-muted">No credit memos on this invoice.</p>
              ) : (
                <ul className="mt-3 divide-y divide-[#e7e7e4]">
                  {memoCtx.memos.map((m) => (
                    <li
                      key={m.id}
                      className="flex flex-wrap items-start justify-between gap-3 py-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm">
                          <span className="font-medium tabular-nums text-ink">{formatMoney(m.amount)}</span>{" "}
                          <span className="text-muted">· {reasonLabel(m.reason)}</span>
                        </p>
                        {m.reasonNote && <p className="mt-0.5 text-sm text-muted">{m.reasonNote}</p>}
                        <p className="mt-0.5 text-xs text-muted">
                          Issued {formatDate(m.issuedAt)}
                          {m.issuedByName ? ` · by ${m.issuedByName}` : ""}
                        </p>
                      </div>
                      {canManage && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => voidMemo.mutate(m.id)}
                          disabled={voidMemo.isPending}
                        >
                          Void memo
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {/* Net position — the invoice value net of credits, computed
                  server-side (total − credits). */}
              {memoCtx.memos.length > 0 && (
                <dl className="mt-1 divide-y divide-[#e7e7e4] border-t border-rule text-sm">
                  <TotalsRow label="Credits applied" value={`−${formatMoney(memoCtx.creditedTotal)}`} />
                  <TotalsRow label="Invoice value after credits" value={formatMoney(memoCtx.cap)} emphasis />
                </dl>
              )}
            </section>
          )}
        </div>

        <aside className="space-y-6">
          {id && <VendorCafeInvoiceCard invoiceId={id} />}
          <section className={CARD}>
            <h2 className={CARD_TITLE}>Details</h2>
            <dl className="mt-3 space-y-3 text-sm">
              <DetailRow label="Client">{invoice.clientName}</DetailRow>
              <DetailRow label="Project">
                <Link to={`/projects/${invoice.projectId}`} className="underline-offset-4 hover:underline">
                  {invoice.projectName}
                </Link>
              </DetailRow>
              <DetailRow label="Quote">
                <Link
                  to={`/projects/${invoice.projectId}/quotes/${invoice.orderId}`}
                  className="underline-offset-4 hover:underline"
                >
                  Order #{invoice.orderNumber}
                </Link>
              </DetailRow>
              <DetailRow label="Client PO #">
                <ClientPoField orderId={invoice.orderId} value={invoice.customerPo ?? null} invoiceId={invoice.id} />
              </DetailRow>
              <DetailRow label="Emailed to client">
                {status === "open" ? "Not yet" : emailSent ? "Yes" : "No — link only"}
              </DetailRow>
              <DetailRow label="Card payment">
                {canManage && status !== "paid" && status !== "voided" ? (
                  <label
                    className="inline-flex cursor-pointer items-center gap-2"
                    title="On: the client sees a Pay button and can pay by card. Off: they see your payment terms and are asked to arrange payment (e.g. check)."
                  >
                    <input
                      type="checkbox"
                      role="switch"
                      aria-label="Allow card payment"
                      className="h-4 w-4 accent-ink"
                      checked={invoice.allowCreditCard !== false}
                      disabled={cardPayment.isPending}
                      onChange={(e) => cardPayment.mutate(e.target.checked)}
                    />
                    {invoice.allowCreditCard !== false ? "Allowed" : "Off"}
                  </label>
                ) : invoice.allowCreditCard !== false ? (
                  "Allowed"
                ) : (
                  "Off"
                )}
              </DetailRow>
            </dl>
          </section>
          {id && <SentEmailsCard doc="invoice" id={id} />}
        </aside>
      </div>

      {/* Record payment modal */}
      {showPayModal && payCtx && (
        <Modal
          title="Record payment"
          subtitle={`${formatMoney(payCtx.outstanding)} outstanding on invoice #${invoice.invoiceNumber}.`}
          onClose={() => setShowPayModal(false)}
        >
          <div className="space-y-4">
            <label className="block text-xs text-muted">
              Amount received (required)
              <input
                type="number"
                min="0"
                step="0.01"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="0.00"
                className={FIELD}
              />
              <span className="mt-1 block">
                Enter the amount actually received — a part payment is fine.
              </span>
            </label>

            <label className="block text-xs text-muted">
              Method
              <select
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value as PaymentMethod)}
                className={FIELD}
              >
                {METHOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-xs text-muted">
              Date received
              <input
                type="date"
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
                className={DATE_FIELD}
              />
            </label>

            <label className="block text-xs text-muted">
              Reference
              <input
                type="text"
                value={payReference}
                onChange={(e) => setPayReference(e.target.value)}
                placeholder="Check number, wire ref, confirmation code"
                className={FIELD}
              />
            </label>

            <label className="block text-xs text-muted">
              Note
              <textarea
                value={payNote}
                onChange={(e) => setPayNote(e.target.value)}
                rows={2}
                placeholder="Anything the next person reconciling should know"
                className={TEXTAREA}
              />
            </label>

            {payError && (
              <p role="alert" className="rounded-md border border-risk/30 bg-risk-bg px-3 py-2 text-sm text-risk">
                {payError}
              </p>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowPayModal(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={recordPayment.isPending}
              onClick={() => {
                setPayError(null);
                const cents = dollarsToCents(payAmount);
                if (cents === null || cents <= 0) {
                  setPayError("Enter a payment amount greater than $0.");
                  return;
                }
                // A client-side courtesy check only — the server recomputes
                // the outstanding balance in-transaction and refuses an
                // overpayment there, which is the check that counts.
                if (cents > payCtx.outstandingCents) {
                  setPayError(
                    `That is more than the ${formatMoney(payCtx.outstanding)} outstanding. Record the amount actually received.`,
                  );
                  return;
                }
                const reference = payReference.trim();
                const note = payNote.trim();
                recordPayment.mutate({
                  amount_cents: cents,
                  method: payMethod,
                  paid_at: payDate,
                  ...(reference ? { reference } : {}),
                  ...(note ? { note } : {}),
                });
              }}
            >
              {recordPayment.isPending ? "Saving…" : "Record payment"}
            </Button>
          </div>
        </Modal>
      )}

      {/* Create credit memo modal */}
      {showMemoModal && memoCtx && (
        <Modal
          title="Create credit memo"
          subtitle={`Up to ${formatMoney(memoCtx.cap)} creditable on this invoice.`}
          onClose={() => setShowMemoModal(false)}
        >
          <div className="space-y-4">
            <label className="block text-xs text-muted">
              Amount (required)
              <input
                type="number"
                min="0"
                step="0.01"
                value={memoAmount}
                onChange={(e) => setMemoAmount(e.target.value)}
                placeholder="0.00"
                className={FIELD}
              />
            </label>

            <label className="block text-xs text-muted">
              Reason
              <select
                value={memoReason}
                onChange={(e) => setMemoReason(e.target.value as CreditMemoReason)}
                className={FIELD}
              >
                {REASON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-xs text-muted">
              Note (required)
              <textarea
                value={memoNote}
                onChange={(e) => setMemoNote(e.target.value)}
                rows={3}
                placeholder="Why is this credit being issued? (audit trail)"
                className={TEXTAREA}
              />
            </label>

            <label className="block text-xs text-muted">
              Date
              <input
                type="date"
                value={memoDate}
                onChange={(e) => setMemoDate(e.target.value)}
                className={DATE_FIELD}
              />
            </label>

            {memoError && (
              <p role="alert" className="rounded-md border border-risk/30 bg-risk-bg px-3 py-2 text-sm text-risk">
                {memoError}
              </p>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowMemoModal(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={createMemo.isPending}
              onClick={() => {
                setMemoError(null);
                const note = memoNote.trim();
                if (note.length === 0) {
                  setMemoError("A note is required for the audit trail.");
                  return;
                }
                const cents = dollarsToCents(memoAmount);
                if (cents === null || cents <= 0) {
                  setMemoError("Enter a credit amount greater than $0.");
                  return;
                }
                if (cents > memoCtx.capCents) {
                  setMemoError(
                    "That amount is more than the invoice's remaining creditable balance.",
                  );
                  return;
                }
                createMemo.mutate({
                  amount_cents: cents,
                  reason: memoReason,
                  reason_note: note,
                  issued_at: memoDate,
                });
              }}
            >
              {createMemo.isPending ? "Saving…" : "Create credit memo"}
            </Button>
          </div>
        </Modal>
      )}

      {/* Send to client — the same send window as the quote. */}
      {showSendModal && (
        <SendToCustomerModal
          title={`Send Invoice #${invoice.invoiceNumber}`}
          subtitle="Email the invoice link to the client and anyone you'd like to copy."
          clientId={invoice.clientId}
          ctaLabel={missingLinkConfirmPending ? "Send anyway" : "Send"}
          ctaVariant={missingLinkConfirmPending ? "destructive" : "primary"}
          submitting={transition.isPending}
          errorMessage={sendModalMsg}
          noticeMessage={sendModalNotice}
          showBodyEditor
          composeUrl={`/invoices/${id}/send-compose`}
          bodyLabel="Email to the client"
          linkNoun="invoice link"
          currentUserEmail={user?.email ?? null}
          // Same as the quote's send window (Stephanie 2026-11-02): a preview of
          // what the client will see, and "Send me a copy" (BCC'd on the email).
          preview={<PublicInvoicePage previewUuid={invoice.publicShareUuid} />}
          showCopyToMe
          onCancel={() => {
            if (transition.isPending) return;
            closeSendModal();
          }}
          onSubmit={(recipients, options) => {
            setSendModalMsg(null);
            transition.mutate({
              event: "send_to_client",
              recipients,
              body_html: options?.bodyHtml,
              skip_missing_link_check: missingLinkConfirmPending,
              ...(options?.sendCopyToMe ? { send_copy_to_me: true } : {}),
              ...(options?.bcc && options.bcc.length > 0 ? { bcc: options.bcc } : {}),
            });
          }}
        />
      )}
    </div>
  );
}

// ─── Local layout pieces (handoff record grammar) ─────────────────────────
const CARD = "rounded-md border border-rule bg-white p-[22px]";
const CARD_TITLE = "text-lg font-semibold tracking-[-0.4px] text-ink";
const FIELD =
  "mt-1 block h-10 w-full rounded border border-rule bg-white px-3 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]";
// iPhone Safari draws date boxes wider than the screen and centred unless
// told otherwise (Stephanie's phone, 2026-10-13).
const DATE_FIELD = `${FIELD} min-w-0 max-w-full appearance-none text-left`;
const TEXTAREA =
  "mt-1 block w-full rounded border border-rule bg-white px-3 py-2 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]";

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 text-right text-ink">{children}</dd>
    </div>
  );
}

function TotalsRow({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <dt className={emphasis ? "font-medium text-ink" : "text-muted"}>{label}</dt>
      <dd className={cn("tabular-nums", emphasis ? "font-semibold text-ink" : "text-ink")}>{value}</dd>
    </div>
  );
}

function Modal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-md bg-white p-6 shadow-xl sm:rounded-md">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted hover:bg-canvas hover:text-ink"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
